import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A punchcard chart is a matrix-style visualization that uses circles or dots of varying
          sizes to represent data values at the intersections of rows and columns. It effectively
          combines categorical and quantitative data, making it ideal for showcasing patterns,
          trends, or intensity levels across two dimensions. Punchcard charts are commonly used for
          time-based activity tracking, resource allocation, or comparative analysis.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Punchcard charts are effective for visualizing data patterns over time, especially in
          relation to specific intervals:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Activity Patterns Over Time:</strong> Punchcard charts are great for
            identifying trends or patterns based on time, such as user activity or transactions by
            hour and day, helping to detect peak periods or off-peak times.
          </li>
          <li>
            <strong>Highlighting Temporal Patterns:</strong> They can reveal hidden patterns in
            data, such as time-of-day trends or weekly cycles, making it easier to understand
            recurring events or fluctuations in activity.
          </li>
          <li>
            <strong>Tracking Recurring Events:</strong> Punchcard charts are ideal for tracking
            events or occurrences that follow a regular cycle, such as website traffic, sales, or
            customer service inquiries, allowing for straightforward identification of time-based
            trends.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Punchcard charts are a unique way of visualizing time-based data, but they have some
          limitations that should be kept in mind:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Data Context:</strong> Punchcard charts excel at showing data patterns
            over time but may lack the ability to provide detailed context or trends. This can make
            it difficult to draw precise conclusions or make comparisons across different time
            periods.
          </li>
          <li>
            <strong>Overcrowding with Complex Data:</strong> When there is a large amount of data or
            multiple variables, punchcard charts can become cluttered, making it hard to focus on
            specific patterns or key insights.
          </li>
          <li>
            <strong>Difficulty in Representing Large Time Ranges:</strong> Punchcard charts are
            typically designed to display a specific timeframe, often in hourly or daily intervals,
            making it less suitable for visualizing longer-term trends or data that spans over
            extended periods.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in Punchcard charts is essential for distinguishing patterns and
          highlighting significant data points. Using distinct colors for different categories or
          data sets helps viewers easily interpret the chart. Gradients or varying intensities of a
          single hue can indicate frequency or magnitude effectively. Careful consideration of the
          color scheme ensures that the chart remains uncluttered, visually appealing, and intuitive
          to analyze.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Punchcard charts are unique in representing periodic data, but they come with specific
          challenges that must be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Data Context:</strong> While punchcards highlight frequency or
            intensity, they provide little context for underlying causes or additional data
            dimensions, potentially limiting comprehensive analysis.
          </li>
          <li>
            <strong>Overcrowding with Dense Data:</strong> When datasets include numerous categories
            or time points, the chart can become overcrowded, making it hard to distinguish between
            individual data points.
          </li>
          <li>
            <strong>Interpretation Requires Familiarity:</strong> For viewers unfamiliar with the
            format, punchcard charts can be less intuitive compared to other visualization types,
            requiring additional explanation to convey their meaning effectively.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
